import { useToolGroups } from "@/composables/tools/useToolGroups";
import { useToolRouting } from "@/composables/tools/useToolRouting";
import { useToolValidation } from "@/composables/tools/useToolValidation";
import type { App } from "@/services/fobizzToolsApi";
import { useAppsStore } from "@/stores/apps";
import { useUserStore } from "@/stores/user";
import type { BootstrapIconName } from "@/types/bootstrap-icons";
import type { MenuItem } from "@/types/folders";
import type { StartPageSection, Tool } from "@/types/tools";
import { isBackendTool } from "@/types/tools";

/**
 * A single composable to fetch, filter, group, and return your tools.
 */
export function useTools() {
  const { useAppsQuery } = useStartPageQueries();
  const userStore = useUserStore();
  const appsStore = useAppsStore();
  const { TOOL_DEFINITIONS, getToolTranslation, validateToolDefinition } =
    useToolValidation();
  const { getAllGroups } = useToolGroups();
  const { getShowUrl, getInfoUrl, getClientPath } = useToolRouting();

  // Query for apps with SSR fallback
  const { data: toolsFetch } = useAppsQuery();
  const toolsSSR = ref<App[]>(appsStore.apps || []);

  /**
   * The final array of apps (from query or SSR fallback) with source property
   */
  const tools = computed(() =>
    (toolsFetch.value || toolsSSR.value).map((app) => ({
      ...app,
      source: "backend" as const,
    }))
  );

  /**
   * Filter out apps that are disabled or not startpageEnabled
   */
  const filteredTools = computed(() =>
    tools.value.filter(
      (app) => app.startpageEnabled && !userStore.isDisabledTool(app.namespace)
    )
  );

  /**
   * Build start page sections by iterating over the unified toolOrder.
   * For each item, if it is a manual tool, look it up from TOOL_DEFINITIONS.
   * If it is a backend tool, find it in the filteredTools.
   */
  const startPageSections = computed<StartPageSection[]>(() => {
    Object.values(TOOL_DEFINITIONS).forEach(validateToolDefinition);
    const groups = getAllGroups();

    return groups.map((group) => {
      const orderedTools = group.toolOrder
        .map((item) => {
          if (item.type === "manual") {
            const definition = TOOL_DEFINITIONS[item.key];
            if (!definition) {
              console.warn(
                `Missing tool definition for manual key: ${item.key}`
              );
              return null;
            }
            return {
              ...definition,
              name: getToolTranslation(definition.namespace),
            } as Tool;
          } else {
            // Backend tool
            const backendTool = filteredTools.value.find(
              (app) => app.id === item.id
            );

            if (!backendTool) return null;
            return {
              ...backendTool,
              source: "backend" as const,
            } as Tool;
          }
        })
        .filter((tool): tool is Tool => tool !== null);

      return {
        title: group.title,
        badge: group.badge,
        tools: orderedTools,
      };
    });
  });

  /**
   * Create MenuItems for dropdown navigation
   */
  const dropdownItems = computed<MenuItem[]>(() => {
    return startPageSections.value.map((section) => {
      const children = section.tools.map<MenuItem>((tool) => {
        const isAuthenticatedPath =
          userStore.isAuthenticated && !userStore.isVisitor;

        const clientPath = isAuthenticatedPath
          ? getClientPath(tool)
          : undefined;

        const menuItem: MenuItem = {
          label: tool.name || "",
          icon: ((tool.icon || "").replace("bi-", "") ||
            "gear") as BootstrapIconName,
          iconColor: tool.color,
          to: clientPath,
          href: isAuthenticatedPath ? undefined : tool.infoUrl,
          appData: tool,
          features: {
            templates: isBackendTool(tool) ? tool.supportsTemplates : false,
            ai: isBackendTool(tool) ? tool.supportsAiGeneration : false,
          },
        };

        return menuItem;
      });

      return {
        label: section.title,
        children,
      };
    });
  });

  return {
    tools,
    filteredTools,
    startPageSections,
    dropdownItems,
    getShowUrl,
    getInfoUrl,
  };
}
